'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';
import { Heading } from '../ui/heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { IoStarSharp } from 'react-icons/io5';
import { Paragraph } from '../ui/paragraph';
import { fetchStoreReviews } from '@eliadmin/core/src/api/general/general-api';
import type { StoreReview } from '@eliadmin/core/src/api/general/general-api';
import { useQuery } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';

interface InfiniteCarouselProps {
  speed?: number;
  title: string;
}

export function GoogleReviews({ speed = 100, title }: InfiniteCarouselProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const trackRef = useRef<HTMLDivElement>(null);
  const controls = useAnimationControls();
  const [trackWidth, setTrackWidth] = useState(0);
  const [isReducedMotion, setReducedMotion] = useState(false);

  const { data: reviews = { data: [] }, isLoading } = useQuery({
    queryKey: ['REVIEWS', locale],
    queryFn: () => fetchStoreReviews({ locale }),
  });

  const duplicated = [...reviews.data, ...reviews.data, ...reviews.data];

  const measure = useCallback(() => {
    if (!trackRef.current) return;
    const third = trackRef.current.scrollWidth / 3;
    setTrackWidth(third);
  }, []);

  useEffect(() => {
    measure();
    const handler = () => measure();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [measure]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(media.matches);

    const listener = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    if (!trackWidth || isReducedMotion) return;

    const duration = speed;

    controls.set({ x: 0 });
    controls.start({
      x: -trackWidth,
      transition: {
        duration,
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });
  }, [trackWidth, speed, controls, isReducedMotion]);

  return (
    <section
      className={cn(
        'custom-section',
        'relative gap-8 bg-[#EFF3F1] px-0 py-16 sm:gap-10 sm:py-20 md:py-24 lg:gap-12 lg:py-32 xl:py-36',
      )}
    >
      {isLoading ? (
        <GenericLoader />
      ) : (
        <>
          <div
            className={cn(
              'absolute bottom-[116px] left-0 z-[20] h-[276px] w-full max-w-[30px] sm:bottom-[132px] sm:h-[292px] sm:max-w-14 md:bottom-24 md:max-w-20 lg:bottom-32 lg:h-[320px] lg:max-w-32 xl:bottom-36 xl:max-w-[160px]',
              'ea-google-reviews__left-gradient',
            )}
          />
          <div
            className={cn(
              'custom-container',
              'relative z-30 flex flex-row items-end justify-between px-4 2xl:px-0',
            )}
          >
            <div className="flex w-full flex-col gap-6 md:max-w-[800px] md:flex-row md:gap-4">
              <div className="flex flex-col items-start justify-start gap-6 sm:gap-4">
                <Heading title={title} />
                <div className="flex flex-col items-start justify-start gap-6 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex flex-col items-start justify-start gap-4 sm:flex-row sm:items-center sm:gap-2">
                    <div className="flex items-center justify-start gap-2">
                      <Image
                        src="/images/google.svg"
                        alt="Google"
                        width={20}
                        height={20}
                        className="object-contain"
                      />
                      <div className="flex items-center justify-start gap-1">
                        {[...Array(5)].map((_, i) => (
                          <IoStarSharp key={i} className="text-[#EDB000]" size={16} />
                        ))}
                      </div>
                    </div>
                    <Paragraph>320+ {t('text3207')}</Paragraph>
                  </div>
                </div>
              </div>
            </div>

            <Button asChild variant="white" className="hidden whitespace-nowrap text-black md:flex">
              <Link prefetch={false} href="/">
                <Image
                  src="/images/google.svg"
                  alt="Google"
                  width={24}
                  height={24}
                  className="object-contain"
                />
                {t('text585')}
              </Link>
            </Button>
          </div>

          <div className="relative overflow-hidden">
            <motion.div
              ref={trackRef}
              className="flex"
              style={{ willChange: 'transform' }}
              animate={controls}
            >
              {duplicated.map((review, idx) => (
                <div
                  key={`${review.id}-${idx}`}
                  className="mx-4 flex-shrink-0"
                  style={{ width: 'fit-content' }}
                >
                  <ReviewCard review={review} />
                </div>
              ))}
            </motion.div>
          </div>
          <div className="relative z-30 flex w-full items-center justify-center px-4 md:hidden">
            <Button asChild variant="white" className="w-full text-black sm:w-fit">
              <Link prefetch={false} href="/">
                <Image
                  src="/images/google.svg"
                  alt="Google"
                  width={24}
                  height={24}
                  className="object-contain"
                />
                {t('text585')}
              </Link>
            </Button>
          </div>
          <div
            className={cn(
              'absolute bottom-16 right-0 z-[20] h-[276px] w-full max-w-[30px] sm:bottom-20 sm:h-[292px] sm:max-w-14 md:bottom-24 md:max-w-20 lg:bottom-32 lg:h-[320px] lg:max-w-32 xl:bottom-36 xl:max-w-[160px]',
              'ea-google-reviews__right-gradient',
            )}
          />
        </>
      )}
    </section>
  );
}

function ReviewCard({ review }: { review: StoreReview }) {
  return (
    <div className="flex w-[300px] flex-col items-start justify-between gap-6 rounded-[12px] bg-white p-6 sm:w-[380px] sm:p-7 md:w-[420px] lg:w-[486px] lg:p-8">
      <div className="flex items-center justify-start gap-3 sm:gap-4">
        <div className="flex items-center justify-start gap-1">
          {[...Array(5)].map((_, i) => (
            <IoStarSharp key={i} className="text-[#EDB000]" size={16} />
          ))}
        </div>
        <Paragraph className="text-sm">5/5 Google review</Paragraph>
      </div>

      <Paragraph
        className={cn(
          'text-base font-normal text-black sm:text-lg lg:text-xl xl:text-[28px]',
          'ea-google-reviews__description',
        )}
        innerHtml={review.review}
      />
      <Paragraph className="text-sm font-medium text-black lg:text-base">
        {review.name ?? 'Jaroslava K.'}
      </Paragraph>
    </div>
  );
}
